import type { BlocksContent } from '@strapi/blocks-react-renderer';
import type { ContextEntities } from './RenderContext';
import { readPath } from './substitutePlaceholders';

// A paragraph whose entire content is one `{path.to.field}` token — the only
// shape the block-level splice recognizes. Same token grammar as the inline
// `PLACEHOLDER_RE` in substitutePlaceholders.ts.
const BLOCK_PLACEHOLDER_RE = /^\{([\w.[\]-]+)\}$/;

/**
 * Block-level placeholder resolution for rich-text-blocks entity fields
 * (10.02.04 § Page Composition): a paragraph consisting solely of e.g.
 * `{city.intro}` is replaced by the blocks stored on that entity field,
 * formatting preserved — the counterpart to inline substitution, which can
 * only splice strings into text nodes and passes object values through as
 * their literal token.
 *
 * Single pass over the top-level nodes only: spliced-in content is not
 * re-scanned (no recursion, so a field containing its own token cannot
 * loop), and a token used inline within a sentence stays subject to the
 * inline rules. A path that resolves to anything other than a non-empty
 * blocks array leaves the paragraph untouched.
 */
export function spliceBlockPlaceholders(
  nodes: BlocksContent,
  entities: ContextEntities,
): BlocksContent {
  return nodes.flatMap((node) => {
    const path = soleTokenOf(node);
    if (!path) {
      return [node];
    }
    const value = readPath(entities, path);
    if (isBlocksContent(value)) {
      return value;
    }
    return [node];
  }) as BlocksContent;
}

/** The `{path}` when the node is a paragraph made of exactly that token. */
function soleTokenOf(node: unknown): string | undefined {
  if (node == null || typeof node !== 'object') {
    return undefined;
  }
  const block = node as { type?: string; children?: unknown[] };
  if (block.type !== 'paragraph' || block.children?.length !== 1) {
    return undefined;
  }
  const child = block.children[0] as { type?: string; text?: unknown };
  if (child?.type !== 'text' || typeof child.text !== 'string') {
    return undefined;
  }
  return BLOCK_PLACEHOLDER_RE.exec(child.text.trim())?.[1];
}

function isBlocksContent(value: unknown): value is BlocksContent {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (block) =>
        block != null &&
        typeof block === 'object' &&
        typeof (block as { type?: unknown }).type === 'string',
    )
  );
}
